import React, {
  FormEvent, useCallback, useContext, useState,
} from 'react';
import Box from '@mui/material/Box';
import { UnitsOrder } from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsOrder';
import { translations } from 'src/constants/translations';
import {
  GroupingByProject,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/GroupingByProject';
import { UnitsCard } from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCard';
import {
  UnitsCardTextBox,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardTextBox';
import {
  UnitsCardMainSecondaryText,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardMainSecondaryText';
import {
  UnitsCardHeaderWithDivider,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardHeaderWithDivider';
import {
  UnitsCardAdditionalInfoWithDivider,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardAdditionalInfoWithDivider';
import {
  UnitsCardIconsBlockOne,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardIconsBlockOne';
import {
  UnitsCardIconsBlockTwo,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardIconsBlockTwo';
import {
  UnitsCardFooterDefault,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardFooterDefault';
import {
  UnitsCardFooterHoverActions,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardFooterHoverActions';
import {
  UnitsCardFooterHoverDetails,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/components/UnitsCardFooterHoverDetails';
import { IMarketplace, IMarketplaceModule } from 'src/components/profileComponents/Marketplace/types';
import { ButtonApp } from 'src/components/Button';
import { Breadcrumbs, ICrumbs } from 'src/components/profileComponents/Breadcrumbs';
import {
  UnitsDataContext,
} from 'src/components/profileComponents/Marketplace/components/UnitsTiles/context/UnitsDataProvider';
import { pathList } from 'src/containers/Profile/routeList';
import { useToastr } from 'src/hooks/useToastr';
import { useGetAuthData } from 'src/hooks';
import { putMarketplaceModule } from 'src/api/marketplace';
import { useMarketplaces } from 'src/hooks/marketplace';
import { useStyles } from './styles';

export const UnitsContextWrapper: React.FC<{ marketplace?: IMarketplace, setError: (error: string) => void }> = ({ marketplace, setError }) => {
  const { getJWT } = useGetAuthData();
  const jwt = getJWT();
  const { fetchMarketplaces } = useMarketplaces();
  const [isLoading, setIsLoading] = useState(false);
  const context = useContext(UnitsDataContext);
  const { toastrClear, toastrSuccess } = useToastr();

  const crumbs: ICrumbs[] = [
    {
      name: translations.marketplace,
      url: `/profile/${pathList.marketplace}`,
    },
    {
      name: `${marketplace?.name[marketplace?.language]} set-up`,
      url: `/profile/${pathList.marketplace}/${marketplace?.id}`,
    },
    {
      name: 'Units tile',
    },
  ];

  const onSave = useCallback(async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (marketplace && context) {
      setIsLoading(true);
      const saveData: Partial<IMarketplaceModule> = {
        '@id': marketplace.frontConfiguration.module['@id'],
        '@type': marketplace.frontConfiguration.module['@type'],
        id: marketplace.frontConfiguration.module.id,
        placementTile: {
          ...marketplace.frontConfiguration.module.placementTile,
          card: {
            ...context.state.unitsCard,
          },
          propertiesSortOrder: context.state.unitsOrder,
        },
      };

      const res = await putMarketplaceModule(jwt, marketplace.id, saveData);

      if (res.ok) {
        toastrClear();
        toastrSuccess();
        fetchMarketplaces();
        setIsLoading(false);
      } else {
        setError(res.data['hydra:description']);
        setIsLoading(false);
      }
    }
  }, [marketplace, context]);

  const styles = useStyles();
  return (
    <form onSubmit={onSave}>
      <Box mb={8} display="flex" flexWrap="nowrap">
        <Box fontSize="32px" mb={1} fontWeight={700} mr="auto">
          { translations.unitsTitle }
        </Box>
        <div>
          <ButtonApp
            text={translations.saveChanges}
            color="primary"
            variant="contained"
            type="submit"
            disabled={isLoading}
          />
        </div>
      </Box>
      <Box mb={5}>
        <Breadcrumbs crumbs={crumbs} />
      </Box>
      <>
        <div className={styles.tilesWrapper}>
          <div className="units-order">
            <Box fontSize="24px" mb={8} fontWeight={700}>
              { translations.unitsOrder }
            </Box>
            <div className={styles.section}>
              <UnitsOrder />
            </div>
          </div>
        </div>
        <div className={styles.tilesWrapper}>
          <Box fontSize="24px" mb={8} fontWeight={700}>
            { translations.groupingByProjects }
          </Box>
          <span>Upload the projects cover for grouped tile view (JPEG, 2320×424 px)</span>
          <div className={styles.section}>
            <GroupingByProject />
          </div>
        </div>
        <div>
          <Box fontSize="24px" mb={8} fontWeight={700}>
            { translations.unitsCard }
          </Box>
          <div className={styles.tilesWrapper}>
            <div className={styles.section}>
              <UnitsCard marketplace={marketplace} />
            </div>
            <div className={styles.section}>
              <UnitsCardTextBox />
            </div>
            <div className={styles.section}>
              <UnitsCardMainSecondaryText />
            </div>
            <div className={styles.section}>
              <UnitsCardHeaderWithDivider />
            </div>
            <div className={styles.section}>
              <UnitsCardAdditionalInfoWithDivider />
            </div>
            <div className={styles.section}>
              <UnitsCardIconsBlockOne />
            </div>
            <div className={styles.section}>
              <UnitsCardIconsBlockTwo />
            </div>
            <div className={styles.section}>
              <UnitsCardFooterDefault />
            </div>
            <div className={styles.section}>
              <UnitsCardFooterHoverActions />
            </div>
            <div className={styles.section}>
              <UnitsCardFooterHoverDetails />
            </div>
          </div>
        </div>
      </>
    </form>
  );
};
